import { Controller } from "@hotwired/stimulus";
import { i18n } from "../../../utils/i18n";
import { type Datepicker } from "vanillajs-datepicker";

import { mergeProducts, type MergedProductsData, toBaseUnit } from "./lib";
import DynamiNestedFormController from "../../dynamic_nested_form_controller";

export default class extends Controller<HTMLDivElement> {
  static targets = [
    "receivableProduct",
    "productToReceiveInput",
    "locationInput",
    "dateInput",
    "nestedFormController"
  ];

  static values = {
    receivedProducts: Object
  };

  declare readonly hasReceivableProductTarget: boolean;
  declare readonly receivableProductTarget: HTMLInputElement;
  declare readonly receivableProductTargets: HTMLInputElement[];

  declare readonly hasProductToReceiveInputTarget: boolean;
  declare readonly productToReceiveInputTarget: HTMLSelectElement;
  declare readonly productToReceiveInputTargets: HTMLSelectElement[];

  declare receivedProductsValue: Record<string, number>;
  declare readonly hasReceivedProductsValue: boolean;

  declare readonly hasLocationInputTarget: boolean;
  declare readonly locationInputTarget: HTMLSelectElement;

  declare readonly nestedFormControllerTarget: HTMLDivElement;
  declare readonly hasNestedFormControllerTarget: boolean;

  receivableProducts: MergedProductsData[] = [];

  connect(): void {
    if (
      this.hasReceivableProductTarget &&
      this.receivableProductTargets.length
    ) {
      this.calculateReceivableProducts();
    }
  }

  // productToReceiveInput is the select that will be populated with the available products
  productToReceiveInputTargetConnected(select: HTMLSelectElement) {
    this.populateWithAvailableProducts();

    // Update the code input when the product select
    select.addEventListener("change", () => {
      const productId = select.value;
      const productData = this.receivableProducts.find(
        ({ value }) => value === productId
      );

      if (productData) {
        const codeInput = select
          .closest(".document-line-item")
          ?.querySelector<HTMLInputElement>(
            ".purchase_order_purchases_items_code input"
          );

        const quantityInput = select
          .closest(".document-line-item")
          ?.querySelector<HTMLInputElement>(".quantity_with_unit input");

        if (codeInput) {
          codeInput.value = productData.code ?? "";
        }

        if (quantityInput) {
          quantityInput.value = `${productData.pendingQuantity} ${productData.unit}`;
        }
      }
      this.calculateReceivableProducts();
    });

    const parentNode = select.closest(".document-line-item");
    const quantityInput = parentNode?.querySelector<HTMLInputElement>(
      ".quantity_with_unit input"
    );
    if (quantityInput) {
      quantityInput.addEventListener("change", () => {
        this.calculateReceivableProducts();
      });
    }

    const locationInput = parentNode?.querySelector<HTMLSelectElement>(
      ".purchase_order_purchases_location input[type=hidden]"
    );
    if (locationInput) {
      locationInput.value = this.locationInputTarget.value;
    }
  }

  productToReceiveInputTargetDisconnected() {
    this.calculateReceivableProducts();
  }

  dateInputTargetConnected(
    input: HTMLInputElement & { datepicker?: Datepicker }
  ) {
    input.value = i18n.l("date.formats.default", new Date());
    if (input.datepicker) {
      input.datepicker.update();
    }
  }

  // receivableProductTarget is the hidden input that will be used to obtain the available
  // products
  receivableProductTargetConnected(input: HTMLInputElement) {
    if (input.value) {
      this.calculateReceivableProducts();
    }

    const parentNode = input.closest(".document-line-item");

    const codeInput = parentNode?.querySelector<HTMLInputElement>(
      ".purchase_order_items_code input"
    );
    if (codeInput) {
      codeInput.addEventListener("change", () => {
        this.calculateReceivableProducts();
      });
    }

    const quantityInput = parentNode?.querySelector<HTMLInputElement>(
      ".quantity_with_unit input"
    );
    if (quantityInput) {
      quantityInput.addEventListener("change", () => {
        this.calculateReceivableProducts();
      });
    }
  }

  receivableProductTargetDisconnected() {
    this.calculateReceivableProducts();
  }

  populateWithAvailableProducts() {
    this.productToReceiveInputTargets.forEach((select) => {
      const currentValue = select.value;

      const pendingItems = this.receivableProducts.filter(
        ({ pendingQuantity, value }) =>
          pendingQuantity > 0 || currentValue === value
      );

      if (
        (currentValue !== "" &&
          this.receivableProducts.find(
            ({ value }) => currentValue === value
          )) ||
        pendingItems.length === 0
      ) {
        select.innerHTML = "";
      } else {
        select.innerHTML = "<option value=''></option>";
      }
      pendingItems.forEach(({ value, name }) => {
        const option = document.createElement("option");
        option.value = value;
        option.text = name ?? "";
        select.appendChild(option);
      });
      select.value = currentValue;
    });
  }

  calculateReceivableProducts() {
    if (!document) {
      // do not run this code during the tests tear down
      return;
    }
    this.receivableProducts = mergeProducts(
      this.receivableProductTargets.map((input) => {
        const lineElement = input.closest(".document-line-item");
        const codeInput = lineElement?.querySelector<HTMLInputElement>(
          ".purchase_order_items_code input"
        );

        const quantityInput = lineElement?.querySelector<HTMLInputElement>(
          ".quantity_with_unit input[type=text]"
        );
        const baseUnit = input.dataset.unitOfMeasure ?? "";
        const equivalencies = JSON.parse(input.dataset.equivalencies ?? "[]");

        const receivedQuantity = this.productToReceiveInputTargets
          .filter(({ value }) => value === input.value)
          .map(
            (select) =>
              select
                .closest(".document-line-item")
                ?.querySelector<HTMLInputElement>(
                  ".quantity_with_unit input[type=text]"
                )?.value ?? "0.0"
          )
          .reduce(
            (acc: number, quantityWithUnit: string) =>
              acc +
              toBaseUnit({
                quantityWithUnit,
                baseUnit,
                equivalencies
              }),
            this.receivedProductsValue[input.value] ?? 0
          );

        return [
          input.value,
          {
            value: input.value,
            name: input.dataset.label,
            code: codeInput?.value,
            baseUnit,
            equivalencies,
            quantity: quantityInput?.value ?? "",
            receivedQuantity
          }
        ];
      })
    );

    this.populateWithAvailableProducts();
  }

  autoFill() {
    const nestedFormController =
      this.application.getControllerForElementAndIdentifier(
        this.nestedFormControllerTarget,
        "dynamic-nested-form"
      ) as DynamiNestedFormController;

    if (!nestedFormController) {
      return;
    }

    this.receivableProducts.forEach((product) => {
      if (product.pendingQuantity > 0) {
        nestedFormController.addNestedField((nestedFields) => {
          const productInput = nestedFields.querySelector<HTMLSelectElement>(
            'select[name$="[product_id]"]'
          );
          if (productInput) {
            const option = document.createElement("option");
            option.value = product.value;
            option.text = product.name ?? "";
            productInput.appendChild(option);
            productInput.value = product.value;
          }
          const quantityInput = nestedFields.querySelector<HTMLInputElement>(
            'input[name$="[quantity_with_unit]"]'
          );
          if (quantityInput) {
            quantityInput.value = `${product.pendingQuantity} ${product.unit}`;
          }
          const codeInput = nestedFields.querySelector<HTMLInputElement>(
            'input[name$="[code]"]'
          );
          if (codeInput) {
            codeInput.value = product.code ?? "";
          }
        });
      }
    });
    this.calculateReceivableProducts();
  }
}
